<template>
  <div>
    <el-dialog v-if="visible" :close-on-click-modal="false" :show-close="false" :model-value="visible"
      @update:modelValue="(val) => { if (!val) modal.closePandyTransferItemRecRet(); visible = val }" width="70%">
      <template #title>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span>{{ recRet }}</span>
          <el-button size="small" type="warning" @click="resetForm">Reset</el-button>
        </div>
      </template>

      <el-form :model="pandyTransferItem" ref="formRef" :rules="rules" label-width="160px" size="small">
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="DateTime:" prop="returnedReceivedAt">
              <el-date-picker v-model="pandyTransferItem.returnedReceivedAt" type="datetime" format="YYYY-MM-DD HH:mm"
                value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%" />
            </el-form-item>
            <el-form-item label="Branch:" prop="returnedReceivedPlace">
              <el-select v-model="pandyTransferItem.returnedReceivedPlace" placeholder="Select" style="width: 100%">
                <el-option v-for="item in branchList" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
            <el-form-item label="Slot Code:" prop="returnedReceivedSlotCode">
              <el-autocomplete v-model="pandyTransferItem.returnedReceivedSlotCode"
                :fetch-suggestions="fetchSlotSuggestions" placeholder="Search Slot Code" @select="handleSlotSelect"
                trigger-on-focus style="width: 100%">
                <template #default="scope">
                  <div>{{ scope?.item?.code }}</div>
                </template>
              </el-autocomplete>
            </el-form-item>
            <el-form-item label="SKU:" prop="sku">
              <div @click="modal.openSku()" style="width: 100%">
                <el-input v-model="pandyTransferItem.sku" placeholder="Click to select sku" readonly
                  style="width: 100%" />
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item v-if="recRet !== 'Received'" label="Customer Name:" prop="returnedCustomerSmithName">
              <div @click="modal.openCustomer()" style="width: 100%">
                <el-input v-model="pandyTransferItem.returnedCustomerSmithName" placeholder="Click to select customer"
                  readonly style="width: 100%" />
              </div>
            </el-form-item>
            <el-form-item label="Received Employee:" prop="receivedEmployeeName">
              <div @click="modal.openEmployee()" style="width: 100%">
                <el-input v-model="pandyTransferItem.receivedEmployeeName" placeholder="Click to select employee"
                  readonly style="width: 100%" />
              </div>
            </el-form-item>
            <el-form-item label="Note:" prop="returnedReceivedNote">
              <el-input type="textarea" :rows="3" v-model="pandyTransferItem.returnedReceivedNote"
                style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <el-divider style="margin: 16px 0 12px 0;" />

      <PandyTransferItemTable ref="pandyTransferTable" :item-list="itemList" :is-rec-ret="true"
        :on-detail="handleItemDetail" :rec-ret="recRet" />

      <template #footer>
        <el-button @click="modal.closePandyTransferItemRecRet(); visible = false" size="small">Cancel</el-button>
        <el-button type="primary" @click="handlePandyTransferItemRecRetDialogConfirm" size="small">Confirm</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { isReturnedReceived, ofOptions } from '@/utils/constants'
import { fetchSlotList } from '@/api/slot'
import { useModalStore } from '@/stores/modal'
import { useShopDataStore } from '@/stores/shopData'
import { useLoading } from '@/composables/useLoading'
import PandyTransferItemTable from '@/views/sms/common/components/PandyTransferItemTable.vue'
import { defaultPandyTransferItemRecRet as defaultPandyTransferItem } from './defaults'

const modal = useModalStore()
const shopDataStore = useShopDataStore()
const { setLoading } = useLoading()
const formRef = ref(null)
const pandyTransferTable = ref(null)

const visible = ref(false)
const skuPlace = ref(null)
const employeeBranchList = ref([])
const branchList = ref([])
const itemList = ref([])
const recRet = ref('')
const onConfirm = ref(null)
const pandyTransferItem = ref({ ...defaultPandyTransferItem })

const rules = {
  returnedReceivedPlace: [{ required: true, message: 'This field is required', trigger: 'blur' }],
  returnedReceivedAt: [{ required: true, message: 'This field is required', trigger: 'blur' }],
  returnedReceivedSlotCode: [{ required: true, message: 'This field is required', trigger: 'blur' }],
  returnedCustomerSmithName: [{ required: true, message: 'This field is required', trigger: 'blur' }],
  receivedEmployeeName: [{ required: true, message: 'This field is required', trigger: 'change' }],
  sku: [{ required: true, message: 'This field is required', trigger: 'change' }]
}

watch(() => modal.customer.selected, (val) => {
  if (val && visible.value) {
    pandyTransferItem.value.returnedCustomerSmithName = val.name
    pandyTransferItem.value.returnedCustomerSmithId = val.id
  }
})

watch(() => modal.employee.selected, (val) => {
  if (val && visible.value) {
    pandyTransferItem.value.receivedEmployeeName = val.name
    pandyTransferItem.value.receivedEmployeeId = val.id
    employeeBranchList.value = val.branches || []
  }
})

watch(() => modal.sku.selected, (val) => {
  if (val && visible.value) {
    pandyTransferItem.value.sku = val.code
    skuPlace.value = val.place
  }
})

watch(() => modal.pandyTransferItemRecRet.visible, (val) => {
  if (val) {
    const params = modal.pandyTransferItemRecRet.data
    pandyTransferItem.value = { ...defaultPandyTransferItem }
    branchList.value = params.branchList
    recRet.value = params.recRet
    onConfirm.value = params.onConfirm
    pandyTransferItem.value.returnedReceivedAt = getCurrentDateTime()
    pandyTransferItem.value.returnedReceivedPlace = params.defaultBranchId
    if (params.itemList) itemList.value = params.itemList
    visible.value = true
  } else {
    visible.value = false
  }
}, { immediate: true })

function getCurrentDateTime() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`
}

function resetForm() {
  formRef.value?.resetFields()
}

async function fetchSlotSuggestions(value, cb) {
  try {
    if (value && value.length > 1) {
      const response = await fetchSlotList({ keyword: value })
      cb(response.data.list || [])
    } else {
      cb([])
    }
  } catch (e) {
    cb([])
  }
}

function handleSlotSelect(item) {
  pandyTransferItem.value.returnedReceivedSlotCode = item.code
  pandyTransferItem.value.returnedReceivedPlace = item.branchId
  if (recRet.value === 'Received') {
    const match = shopDataStore.defaultSkuList.find(sku =>
      sku.place === item.branchId && sku.of === ofOptions.GD.id && sku.parentCategoryId === 60
    )
    if (match) pandyTransferItem.value.sku = match.code
  }
}

function handleItemDetail(index, row) {
  modal.openPandyTransferItem({
    pandyTransferItem: { ...row },
    isDetail: true,
    branchList: branchList.value,
    recRet: recRet.value
  })
}

async function handlePandyTransferItemRecRetDialogConfirm() {
  const valid = await formRef.value.validate().then(() => true).catch(() => false)
  if (!valid) return

  if (recRet.value === 'Received' && pandyTransferItem.value.returnedReceivedPlace === itemList.value[0]?.place) {
    ElMessage.error(`${recRet.value} Place and Item Place cannot be the same.`)
    return
  }
  if (skuPlace.value !== pandyTransferItem.value.returnedReceivedPlace) {
    ElMessage.error('Received Place and Sku Place should be the same.')
    return
  }
  const branchExist = employeeBranchList.value.find(b => b.branchId === pandyTransferItem.value.returnedReceivedPlace)
  if (!branchExist) {
    ElMessage.error("Received Employee's branch and Received branch must be the same.")
    return
  }
  if (pandyTransferTable.value?.selectedItems?.length < 1) {
    ElMessage.error('Please select at least one item.')
    return
  }

  ElMessageBox.confirm(`Are you sure you want to return these items?`, 'Warning', {
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
    type: 'warning'
  }).then(async () => {

    const selectedItems = pandyTransferTable.value.selectedItems
    const branchCode = branchList.value.find(b => b.id === pandyTransferItem.value.returnedReceivedPlace)?.branchCode

    selectedItems.forEach(item => {
      item.isReturnedReceived = isReturnedReceived.Yes.id
      item.returnedReceivedAt = pandyTransferItem.value.returnedReceivedAt
      item.returnedReceivedPlace = pandyTransferItem.value.returnedReceivedPlace
      item.returnedReceivedSlotCode = pandyTransferItem.value.returnedReceivedSlotCode
      item.returnedCustomerSmithId = pandyTransferItem.value.returnedCustomerSmithId
      item.returnedCustomerSmithName = pandyTransferItem.value.returnedCustomerSmithName
      item.receivedEmployeeId = pandyTransferItem.value.receivedEmployeeId
      item.receivedEmployeeName = pandyTransferItem.value.receivedEmployeeName
      item.returnedReceivedNote = pandyTransferItem.value.returnedReceivedNote
      item.branchCode = branchCode
    })

    try {
      await onConfirm.value(selectedItems)
      modal.closePandyTransferItemRecRet()
      visible.value = false
    } catch (e) { }
  }).catch(() => { })
}
</script>

<style scoped>
:deep(.el-form-item__content) {
  width: 100%;
}

:deep(.el-date-editor.el-input) {
  width: 100%;
}
</style>
